import { IamSdk } from "lib-klab-sdks";

type RoleListResponse = IamSdk.RoleListResponse;
type RoleDetailResponse = IamSdk.RoleDetailResponse;
type CreateRoleRequest = IamSdk.CreateRoleRequest;
type UpdateRoleRequest = IamSdk.UpdateRoleRequest;
type PermissionTreeResponse = IamSdk.PermissionTreeResponse;
type PermissionModule = IamSdk.PermissionModule;
type PaginationMeta = IamSdk.PaginationMeta;

export const rolesSdkMock = (() => {
  let nextRoleId = 100;

  const roles: RoleDetailResponse[] = [
    {
      roleId: 1,
      name: "Administrador",
      isEnabled: true,
      allowRead: true,
      allowCreate: true,
      allowUpdate: true,
      allowDelete: true,
      permissions: [],
      scopes: [],
      locations: [],
      departments: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as any,
    {
      roleId: 2,
      name: "Agente",
      isEnabled: true,
      allowRead: true,
      allowCreate: false,
      allowUpdate: false,
      allowDelete: false,
      permissions: [],
      scopes: [],
      locations: [],
      departments: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as any,
  ];

  function ensureNextId() {
    const maxId = Math.max(...roles.map((r) => Number(r.roleId ?? 0)), 0);
    nextRoleId = Math.max(nextRoleId, maxId + 1);
  }

  ensureNextId();

  function paginate<T>(
    items: T[],
    page: number,
    size: number,
  ): { slice: T[]; meta: PaginationMeta } {
    const safePage = Math.max(0, Number(page ?? 0));
    const safeSize = Math.max(1, Number(size ?? 10));
    const start = safePage * safeSize;
    const end = start + safeSize;

    const totalElements = items.length;
    const totalPages = Math.max(1, Math.ceil(totalElements / safeSize));

    return {
      slice: items.slice(start, end),
      meta: {
        page: safePage,
        size: safeSize,
        totalElements,
        totalPages,
      },
    };
  }

  function listRoles(page: number, size: number): RoleListResponse {
    const ordered = [...roles].sort(
      (a, b) => Number(b.roleId ?? 0) - Number(a.roleId ?? 0),
    );
    const { slice, meta } = paginate(ordered, page, size);

    return {
      content: slice.map((r) => ({
        roleId: r.roleId,
        name: r.name,
        isEnabled: r.isEnabled,
        allowRead: r.allowRead,
        allowCreate: r.allowCreate,
        allowUpdate: r.allowUpdate,
        allowDelete: r.allowDelete,
        locations: r.locations ?? [],
        departments: r.departments ?? [],
        createdAt: r.createdAt,
      })) as any,
      pagination: meta,
    };
  }

  function getRoleById(roleId: number): RoleDetailResponse {
    const id = Number(roleId);
    const found = roles.find((r) => Number(r.roleId) === id);
    if (!found) {
      throw { status: 404, message: "Role no encontrado" };
    }
    return found;
  }

  function createRole(req: CreateRoleRequest): RoleDetailResponse {
    const created: RoleDetailResponse = {
      roleId: nextRoleId++,
      name: (req as any)?.name ?? "Nuevo rol",
      isEnabled: true,
      allowRead: (req as any)?.allowRead ?? false,
      allowCreate: (req as any)?.allowCreate ?? false,
      allowUpdate: (req as any)?.allowUpdate ?? false,
      allowDelete: (req as any)?.allowDelete ?? false,
      permissions: [],
      scopes: (req as any)?.scopes ?? [],
      locations: [],
      departments: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as any;

    roles.unshift(created);
    return created;
  }

  function updateRole(
    roleId: number,
    req: UpdateRoleRequest,
  ): RoleDetailResponse {
    const id = Number(roleId);
    const idx = roles.findIndex((r) => Number(r.roleId) === id);
    if (idx === -1) {
      throw { status: 404, message: "Role no encontrado" };
    }

    const current = roles[idx];

    const updated: RoleDetailResponse = {
      ...current,
      name: (req as any)?.name ?? current.name,
      allowRead:
        (req as any)?.allowRead !== undefined
          ? !!(req as any)?.allowRead
          : current.allowRead,
      allowCreate:
        (req as any)?.allowCreate !== undefined
          ? !!(req as any)?.allowCreate
          : current.allowCreate,
      allowUpdate:
        (req as any)?.allowUpdate !== undefined
          ? !!(req as any)?.allowUpdate
          : current.allowUpdate,
      allowDelete:
        (req as any)?.allowDelete !== undefined
          ? !!(req as any)?.allowDelete
          : current.allowDelete,
      scopes: (req as any)?.scopes ?? current.scopes,
      updatedAt: new Date().toISOString(),
    } as any;

    roles[idx] = updated;
    return updated;
  }

  function deleteRole(roleId: number): void {
    const id = Number(roleId);
    const idx = roles.findIndex((r) => Number(r.roleId) === id);
    if (idx === -1) {
      throw { status: 404, message: "Role no encontrado" };
    }
    roles.splice(idx, 1);
  }

  const permissionTreeModules: PermissionModule[] = [
    {
      permission_id: 1,
      code: "DASHBOARD",
      name: "Dashboard",
      description: undefined,
      permission_type: "MENU_PARENT",
      is_enabled: true,
      icon: "icon_dashboard",
      order: 1,
      route: "/admin/dashboard",
      children: [],
    },
    {
      permission_id: 2,
      code: "EMPLOYEES",
      name: "Empleados",
      description: undefined,
      permission_type: "MENU_PARENT",
      is_enabled: true,
      icon: "icon_people",
      order: 2,
      route: "#",
      children: [
        {
          permission_id: 10,
          code: "EMPLOYEES-MANAGEMENT",
          name: "Gestión de empleados",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 1,
          route: "/admin/employees/management",
          children: [],
        },
        {
          permission_id: 11,
          code: "EMPLOYEES-SCHEDULE-ASSIGNMENT",
          name: "Asignación de horarios",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 2,
          route: "/admin/employees/schedule-assignment",
          children: [],
        },
        {
          permission_id: 12,
          code: "EMPLOYEES-LEADERS-TEAM",
          name: "Líderes y Empleados asignados",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 3,
          route: "/admin/employees/leaders-team",
          children: [],
        },
        {
          permission_id: 13,
          code: "EMPLOYEES-SETTINGS",
          name: "Configuraciones",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 4,
          route: "/admin/employees/settings",
          children: [],
        },
      ],
    },
    {
      permission_id: 3,
      code: "TIMESHEET",
      name: "Timesheet",
      description: undefined,
      permission_type: "MENU_PARENT",
      is_enabled: true,
      icon: "icon_clipboard-check",
      order: 3,
      route: "/admin/timesheet",
      children: [],
    },
    {
      permission_id: 4,
      code: "REQUESTS",
      name: "Solicitudes",
      description: undefined,
      permission_type: "MENU_PARENT",
      is_enabled: true,
      icon: "icon_requests",
      order: 4,
      route: "#",
      children: [
        {
          permission_id: 14,
          code: "REQUESTS-HOURS-JUSTIFICATION",
          name: "Justificación de horas",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 1,
          route: "/admin/requests/hours-justification",
          children: [],
        },
        {
          permission_id: 15,
          code: "REQUESTS-OVERTIME",
          name: "Horas extra",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 2,
          route: "/admin/requests/overtime",
          children: [],
        },
      ],
    },
    {
      permission_id: 5,
      code: "REPORTS",
      name: "Reportes",
      description: undefined,
      permission_type: "MENU_PARENT",
      is_enabled: true,
      icon: "icon_data",
      order: 5,
      route: "#",
      children: [
        {
          permission_id: 16,
          code: "REPORTS-DOWNLOAD",
          name: "Descarga de reportes",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 1,
          route: "/admin/reports/download",
          children: [],
        },
        {
          permission_id: 17,
          code: "REPORTS-PRODUCTIVITY",
          name: "Reportes de productividad",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 2,
          route: "/admin/reports/productivity",
          children: [],
        },
        {
          permission_id: 18,
          code: "REPORTS-PRODUCTIVITY-RULES",
          name: "Reglas de productividad",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 3,
          route: "/admin/reports/productivity-rules",
          children: [],
        },
        {
          permission_id: 23,
          code: "REPORTS-ALL-APPS-WEBS-LIST",
          name: "Reporte General de Apps y Webs",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 4,
          route: "/admin/reports/all-apps-webs",
          children: [],
        },
        {
          permission_id: 25,
          code: "REPORTS-DEVICES",
          name: "Dispositivos y versiones",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 5,
          route: "/admin/reports/devices",
          children: [],
        },
      ],
    },
    {
      permission_id: 6,
      code: "SETTINGS",
      name: "Configuraciones",
      description: undefined,
      permission_type: "MENU_PARENT",
      is_enabled: true,
      icon: "icon_settings",
      order: 6,
      route: "#",
      children: [
        {
          permission_id: 19,
          code: "SETTINGS-ROLES-PERMISSIONS",
          name: "Roles y permisos",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 1,
          route: "/admin/settings/roles-permissions",
          children: [],
        },
        {
          permission_id: 20,
          code: "SETTINGS-LOCATIONS-APARTMENTS",
          name: "Locaciones y Dptos.",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 2,
          route: "/admin/settings/locations-apartments",
          children: [],
        },
        {
          permission_id: 21,
          code: "SETTINGS-SCHEDULES",
          name: "Horarios",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 3,
          route: "/admin/settings/schedules",
          children: [],
        },
        {
          permission_id: 24,
          code: "SETTINGS-HOLIDAYS",
          name: "Feriados",
          description: undefined,
          permission_type: "MENU_ITEM",
          is_enabled: true,
          icon: undefined,
          order: 4,
          route: "/admin/settings/holidays",
          children: [],
        },
      ],
    },
    {
      permission_id: 22,
      code: "PAYMENTVOUCHER",
      name: "Solicitudes de Colaboradores",
      description: undefined,
      permission_type: "MENU_ITEM",
      is_enabled: true,
      icon: "icon_dollar",
      order: 7,
      route: "/admin/paymentvoucher",
      children: [],
    },
  ];

  function listPermissions(isEnabled?: boolean): PermissionTreeResponse {
    const filtered =
      isEnabled === undefined
        ? permissionTreeModules
        : permissionTreeModules.filter(
            (m) => (m.is_enabled ?? true) === isEnabled,
          );

    return {
      modules: filtered,
    };
  }

  return {
    listRoles,
    getRoleById,
    createRole,
    updateRole,
    deleteRole,
    listPermissions,
  };
})();
